import { FC } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/reduxHooks';
import BookNowPanel, {
  IClinicServicesOption,
} from '../BookNowPanel/BookNowPanel';
import PoweredBySection from 'components/PoweredBySection/PoweredBySection';
import { IClinicBookingData } from '../ClinicDetailsPage';
import {
  updateBookingInformation,
  clearBookingInformation,
  updateStep3,
} from 'redux/bookingFormSlice';
import BookingForm from 'components/BookingForm/BookingForm';
import { addToCartTracking } from 'utils/tracking/metaEventTracking';
import HeaderSectionIframe from './HeaderSectionIframe/HeaderSectionIframe';
import styles from './Iframe.module.scss';
import forwardEvent, { EVENT } from 'utils/tracking/forwardEventTracking';

interface IframeProps {
  date: string;
  setDate: React.Dispatch<React.SetStateAction<string>>;
  clinicServicesOptions: IClinicServicesOption[];
}

const Iframe: FC<IframeProps> = ({ date, setDate, clinicServicesOptions }) => {
  const dispatch = useAppDispatch();
  const { clinicData, isLoading } = useAppSelector(
    (state) => state.clinicDetailsSlice
  );
  const bookingInformation = useAppSelector(
    (state) => state.bookingFormSlice.bookingInformation
  );

  const handleBookClinicAppointment = (data: IClinicBookingData) => {
    forwardEvent({
      googleTagManagerId: clinicData.googleTagManagerId,
      isLoadedInsideIframe: true,
      website: clinicData.website,
      payload: { event: EVENT.APPOINTMENT_SLOT_SELECTED },
    });
    addToCartTracking(clinicData.metaPixelCode);
    dispatch(updateBookingInformation({ type: 'clinic', ...data }));
    dispatch(
      updateStep3({
        insurancesByAvailableDoctors: data.insurancesByAvailableDoctors ?? [],
      })
    );
  };

  const getServiceName = () => {
    if (bookingInformation?.type !== 'clinic') return;
    const service = clinicData.services.find(
      (item) =>
        item.id === bookingInformation.serviceId ||
        item.childServices?.find(
          (service) => service.id === bookingInformation.serviceId
        )
    );
    return service?.name;
  };

  const serviceName = getServiceName();

  if (isLoading) return null;

  if (bookingInformation?.type === 'clinic' && serviceName) {
    return (
      <div>
        <HeaderSectionIframe clinicData={clinicData} />

        <BookingForm
          date={bookingInformation.appointmentDate}
          timeBlock={bookingInformation.timeSlot}
          serviceId={bookingInformation.serviceId}
          clinicId={clinicData.id}
          practitionerId={bookingInformation.practitioner.id}
          operatoryId={bookingInformation.operatoryId}
          dentist={bookingInformation.dentist}
          bookingSummaryInfo={{
            avatar: clinicData.avatar,
            title: clinicData.name,
            clinicName: clinicData.name,
            clinicAddress: clinicData.address,
            clinicEmail: clinicData.email,
            clinicPhoneNumber: clinicData.phone,
            serviceTitle: `${serviceName}${
              bookingInformation.practitioner.isChosen
                ? ` • ${bookingInformation.practitioner.title}`
                : ''
            }`,
          }}
          closeBookingDialog={() => dispatch(clearBookingInformation())}
          chairOptimizations={bookingInformation.chairOptimizations}
          isPreferredDoctor={bookingInformation.practitioner.isChosen}
        />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <HeaderSectionIframe clinicData={clinicData} />
      <BookNowPanel
        clinicData={clinicData}
        clinicServicesOptions={clinicServicesOptions}
        onClickClinicTimeSlot={handleBookClinicAppointment}
        onClickPractitionerTimeSlot={handleBookClinicAppointment}
        date={date}
        setDate={setDate}
      />
      <PoweredBySection />
    </div>
  );
};

export default Iframe;
